import { useState, useRef, useCallback, useEffect } from 'react';
import { apiClient } from '../utils/api';
import { useStreamingSTT } from './useStreamingSTT';

const ANALYSER_FFT_SIZE = 256;
const AUDIO_LEVEL_INTERVAL_MS = 50;
const VOICE_INPUT_ENABLED = import.meta.env.VITE_VOICE_INPUT_ENABLED !== 'false';
const ENABLE_STREAMING = import.meta.env.VITE_VOICE_INPUT_STREAMING === 'true';
const AUTO_SEND_ON_FINAL = import.meta.env.VITE_VOICE_INPUT_AUTO_SEND === 'true';

console.log('[VoiceInput] Enabled:', VOICE_INPUT_ENABLED, '| Streaming:', ENABLE_STREAMING, '| Auto-send:', AUTO_SEND_ON_FINAL);

export function useVoiceInput({ onTranscribed, onTranscriptReady, onError, language = 'auto' }) {
    const [isInitializing, setIsInitializing] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [audioLevel, setAudioLevel] = useState(0);
    const [duration, setDuration] = useState(0);
    const [partialTranscript, setPartialTranscript] = useState('');
    const [noSpeechError, setNoSpeechError] = useState(null);

    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);
    const streamRef = useRef(null);
    const analyserRef = useRef(null);
    const audioContextRef = useRef(null);
    const levelIntervalRef = useRef(null);
    const durationIntervalRef = useRef(null);
    const startTimeRef = useRef(null);
    const modeRef = useRef(ENABLE_STREAMING ? 'streaming' : 'batch');
    const onTranscribedRef = useRef(onTranscribed);
    const onTranscriptReadyRef = useRef(onTranscriptReady);
    const isRecordingRef = useRef(false);
    const streamingRef = useRef(null);

    useEffect(() => {
        onTranscribedRef.current = onTranscribed;
    }, [onTranscribed]);

    useEffect(() => {
        onTranscriptReadyRef.current = onTranscriptReady;
    }, [onTranscriptReady]);

    useEffect(() => {
        isRecordingRef.current = isRecording;
    }, [isRecording]);

    const handleFinalTranscript = useCallback((text) => {
        console.log('[VoiceInput] Final transcript received:', text, 'auto-send:', AUTO_SEND_ON_FINAL, 'isRecording:', isRecordingRef.current);

        if (!text || !text.trim()) {
            return;
        }

        if (!isRecordingRef.current) {
            console.log('[VoiceInput] Not recording, putting text in input');
            onTranscriptReadyRef.current?.(text.trim());
            return;
        }

        if (AUTO_SEND_ON_FINAL) {
            console.log('[VoiceInput] Auto-sending final transcript');
            setIsRecording(false);

            if (durationIntervalRef.current) {
                clearInterval(durationIntervalRef.current);
                durationIntervalRef.current = null;
            }

            streamingRef.current?.disconnect();
            onTranscribedRef.current?.(text.trim());
            setPartialTranscript('');
            setAudioLevel(0);
            setDuration(0);
        } else {
            console.log('[VoiceInput] Showing final in input (user will send manually)');
            setIsRecording(false);

            if (durationIntervalRef.current) {
                clearInterval(durationIntervalRef.current);
                durationIntervalRef.current = null;
            }

            streamingRef.current?.disconnect();
            onTranscriptReadyRef.current?.(text.trim());
            setPartialTranscript('');
            setAudioLevel(0);
            setDuration(0);
        }
    }, []);

    const streaming = useStreamingSTT({
        language,
        onPartialTranscript: (text) => {
            setPartialTranscript(text);
        },
        onFinalTranscript: handleFinalTranscript,
        onAudioLevel: (level) => {
            setAudioLevel(level);
        },
        onError: (err) => {
            const errorMsg = err.message || 'Streaming STT error';
            const isNoSpeechTimeout = errorMsg.includes('chưa nghe rõ') || errorMsg.includes('no speech');

            console.log('[VoiceInput] Error received:', errorMsg, 'isNoSpeechTimeout:', isNoSpeechTimeout);

            setIsRecording(false);
            setIsInitializing(false);
            setIsTranscribing(false);

            if (durationIntervalRef.current) {
                clearInterval(durationIntervalRef.current);
                durationIntervalRef.current = null;
            }

            streamingRef.current?.disconnect();
            setPartialTranscript('');
            setAudioLevel(0);
            setDuration(0);

            if (isNoSpeechTimeout) {
                setNoSpeechError(errorMsg);
            } else {
                onError?.(errorMsg);
            }
        },
        onDisconnected: () => {
            console.log('[VoiceInput] WebSocket disconnected, resetting UI');

            setIsRecording(false);
            setIsInitializing(false);
            setIsTranscribing(false);

            if (durationIntervalRef.current) {
                clearInterval(durationIntervalRef.current);
                durationIntervalRef.current = null;
            }

            setPartialTranscript('');
            setAudioLevel(0);
            setDuration(0);
        },
    });

    useEffect(() => {
        streamingRef.current = streaming;
    }, [streaming]);

    const cleanupBatch = useCallback(() => {
        if (levelIntervalRef.current) {
            clearInterval(levelIntervalRef.current);
            levelIntervalRef.current = null;
        }
        if (durationIntervalRef.current) {
            clearInterval(durationIntervalRef.current);
            durationIntervalRef.current = null;
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }
        analyserRef.current = null;
        mediaRecorderRef.current = null;
        chunksRef.current = [];
        setAudioLevel(0);
        setDuration(0);
    }, []);

    const cleanup = useCallback(() => {
        cleanupBatch();
        setPartialTranscript('');
    }, [cleanupBatch]);

    useEffect(() => {
        return cleanup;
    }, [cleanup]);

    const startRecordingBatch = useCallback(async () => {
        setIsInitializing(true);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    sampleRate: 16000,
                    channelCount: 1,
                    echoCancellation: true,
                    noiseSuppression: true,
                }
            });
            streamRef.current = stream;

            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            audioContextRef.current = audioContext;

            const source = audioContext.createMediaStreamSource(stream);
            const analyser = audioContext.createAnalyser();
            analyser.fftSize = ANALYSER_FFT_SIZE;
            source.connect(analyser);
            analyserRef.current = analyser;

            const dataArray = new Uint8Array(analyser.frequencyBinCount);
            levelIntervalRef.current = setInterval(() => {
                if (!analyserRef.current) return;
                analyserRef.current.getByteFrequencyData(dataArray);
                const avg = dataArray.reduce((sum, v) => sum + v, 0) / dataArray.length;
                setAudioLevel(avg / 255);
            }, AUDIO_LEVEL_INTERVAL_MS);

            const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
                ? 'audio/webm;codecs=opus'
                : MediaRecorder.isTypeSupported('audio/webm')
                    ? 'audio/webm'
                    : '';

            const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
            mediaRecorderRef.current = recorder;
            chunksRef.current = [];

            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    chunksRef.current.push(e.data);
                }
            };

            recorder.start(100);
            startTimeRef.current = Date.now();
            setIsInitializing(false);
            setIsRecording(true);
            setDuration(0);

            durationIntervalRef.current = setInterval(() => {
                if (startTimeRef.current) {
                    setDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
                }
            }, 1000);
        } catch (err) {
            setIsInitializing(false);
            cleanup();
            onError?.(err.message || 'Không thể truy cập microphone, vui lòng cấp quyền');
        }
    }, [cleanup, onError]);

    const startRecordingStreaming = useCallback(async () => {
        setIsInitializing(true);
        setPartialTranscript('');
        setAudioLevel(0);
        setNoSpeechError(null);

        try {
            await streaming.connect();
            startTimeRef.current = Date.now();
            setIsInitializing(false);
            setIsRecording(true);
            setDuration(0);

            durationIntervalRef.current = setInterval(() => {
                if (startTimeRef.current) {
                    setDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
                }
            }, 1000);
        } catch (err) {
            setIsInitializing(false);
            setPartialTranscript('');
            setAudioLevel(0);
            onError?.(err.message || 'Kết nối nhận diện giọng nói thất bại, vui lòng thử lại');
        }
    }, [streaming, onError]);

    const startRecording = useCallback(async () => {
        modeRef.current = ENABLE_STREAMING ? 'streaming' : 'batch';

        if (modeRef.current === 'streaming') {
            await startRecordingStreaming();
        } else {
            await startRecordingBatch();
        }
    }, [startRecordingStreaming, startRecordingBatch]);

    const cancelRecordingBatch = useCallback(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
        }
        setIsInitializing(false);
        setIsRecording(false);
        cleanup();
    }, [cleanup]);

    const cancelRecordingStreaming = useCallback(() => {
        if (durationIntervalRef.current) {
            clearInterval(durationIntervalRef.current);
            durationIntervalRef.current = null;
        }
        streaming.disconnect();
        setIsInitializing(false);
        setIsRecording(false);
        setPartialTranscript('');
        setAudioLevel(0);
        setDuration(0);
    }, [streaming]);

    const cancelRecording = useCallback(() => {
        if (modeRef.current === 'streaming') {
            cancelRecordingStreaming();
        } else {
            cancelRecordingBatch();
        }
    }, [cancelRecordingStreaming, cancelRecordingBatch]);

    const stopAndTranscribeBatch = useCallback(() => {
        if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') {
            return;
        }

        const recorder = mediaRecorderRef.current;

        recorder.onstop = async () => {
            setIsRecording(false);
            setIsTranscribing(true);

            const blob = new Blob(chunksRef.current, {
                type: recorder.mimeType || 'audio/webm',
            });

            if (levelIntervalRef.current) {
                clearInterval(levelIntervalRef.current);
                levelIntervalRef.current = null;
            }
            if (durationIntervalRef.current) {
                clearInterval(durationIntervalRef.current);
                durationIntervalRef.current = null;
            }
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
                streamRef.current = null;
            }

            try {
                const result = await apiClient.transcribeAudio(blob, language);
                if (result.text && result.text.trim()) {
                    onTranscribed?.(result.text.trim());
                } else {
                    onError?.('Không nhận diện được giọng nói');
                }
            } catch (err) {
                onError?.(err.message || 'Chuyển đổi giọng nói thất bại, vui lòng thử lại');
            } finally {
                setIsTranscribing(false);
                cleanup();
            }
        };

        recorder.stop();
    }, [language, onTranscribed, onError, cleanup]);

    const stopAndTranscribeStreaming = useCallback(async () => {
        setIsRecording(false);

        if (durationIntervalRef.current) {
            clearInterval(durationIntervalRef.current);
            durationIntervalRef.current = null;
        }

        const currentText = partialTranscript;

        if (currentText && currentText.trim()) {
            streaming.disconnect();
            onTranscribed?.(currentText.trim());
            setPartialTranscript('');
            setAudioLevel(0);
            setDuration(0);
        } else {
            setIsTranscribing(true);
            try {
                const result = await streaming.stopAndGetFinal();
                if (result.text && result.text.trim()) {
                    onTranscribed?.(result.text.trim());
                } else {
                    onError?.('Không nhận diện được giọng nói');
                }
            } catch (err) {
                onError?.(err.message || 'Chuyển đổi giọng nói thất bại, vui lòng thử lại');
            } finally {
                setIsTranscribing(false);
                setPartialTranscript('');
                setAudioLevel(0);
                setDuration(0);
            }
        }
    }, [streaming, partialTranscript, onTranscribed, onError]);

    const stopAndTranscribe = useCallback(() => {
        if (modeRef.current === 'streaming') {
            stopAndTranscribeStreaming();
        } else {
            stopAndTranscribeBatch();
        }
    }, [stopAndTranscribeStreaming, stopAndTranscribeBatch]);

    const retryRecording = useCallback(() => {
        setNoSpeechError(null);
        startRecording();
    }, [startRecording]);

    const dismissError = useCallback(() => {
        setNoSpeechError(null);
    }, []);

    const injectFileAudio = useCallback(async (audioBlob) => {
        if (!isRecordingRef.current) {
            console.log('[VoiceInput] Cannot inject file - not recording');
            return;
        }

        if (modeRef.current !== 'streaming') {
            console.log('[VoiceInput] Cannot inject file - not in streaming mode');
            return;
        }

        console.log('[VoiceInput] Injecting file audio into stream');
        await streaming.injectFileAudio(audioBlob);
    }, [streaming]);

    return {
        isEnabled: VOICE_INPUT_ENABLED,
        isInitializing,
        isRecording,
        isTranscribing,
        isStreamingMode: ENABLE_STREAMING,
        audioLevel,
        duration,
        partialTranscript,
        noSpeechError,
        startRecording,
        injectFileAudio,
        stopAndTranscribe,
        cancelRecording,
        retryRecording,
        dismissError,
    };
}

export default useVoiceInput;
